import React, { useCallback, useContext, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { createPortal } from 'react-dom';
import {
  Button,
  MenuItem,
  TextField,
  ListItemText,
  OutlinedInput,
  Checkbox,
  Select,
  FormControl,
  Tooltip,
  Grid,
  CircularProgress,
} from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import BillSummaryReportService from 'services/BillSummaryReportService';
import SalespersonsService from 'services/SalesPersonService';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';
import { saveAs } from 'file-saver';
import 'date-fns';
import Info from 'components/utils/Alert/Info';
import BillDetailsDialog from 'components/popups/BillDetailsDialog';
import DateLimitDialog from 'components/popups/DateLimitDialog';
import CustomerService from 'services/CustomerService';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { SelectFilterStyle, SALE_SOURCES, PRINT_TYPES, INVOICE_LAYOUTS, } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import { ShopContext } from 'Context/ShopContext';
import CsvHelper from 'helpers/CsvHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import { filterEndDate } from 'components/utils/FilterEndDate';
import PrintIcon from '@mui/icons-material/Print';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import TerminalService from 'services/TerminalServices';
import DownloadInvoice from 'components/utils/InvoiceDownload/DownloadInvoice';
import SaleInvoice from 'components/utils/InvoiceDownload/SaleInvoice';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useTranslation } from 'react-i18next';

function BillSummaryReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');

  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState(getDefaultStartTime());
  const [filterToDate, setFilterToDate] = useState(filterEndDate());
  const [sorting, setSorting] = useState('default');
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
    sorting,
  });
  const [terminals, setTerminals] = useState([]);
  const [filterTerminal, setFilterTerminal] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [filterSalesperson, setFilterSalesperson] = useState([]);
  const [items, setItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [customers, setCustomers] = useState('');
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [dateLimitPopup, setDateLimitPopup] = useState(false);
  const [filterCustomerGroup, setFilterCustomerGroup] = useState([]);
  const [filterSource, setFilterSource] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    bill_no: {
      title: 'Bill No',
      getValue: (datum) => datum.bill_no || datum.fid,
    },
    date: 'Date',
    time: {
      title: 'Time',
      getValue: (datum) => DateTimeHelpers.formatServerTimeSegment(datum.time),
    },
    customer_name: 'Customer',
    customer_mobile: 'Mobile',
    customer_tax_id: 'Tax ID',
    total: {
      title: 'Total Amount',
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.nc_total || 0);
      },
    },
  };

  function getDefaultStartTime() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      filterFromDate !== prevFilterData.filterFromDate ||
      filterToDate !== prevFilterData.filterToDate ||
      sorting !== prevFilterData.sorting ||
      filterTerminal !== prevFilterData.filterTerminal ||
      filterSalesperson !== prevFilterData.filterSalesperson ||
      filterCustomerGroup !== prevFilterData.filterCustomerGroup ||
      filterSource !== prevFilterData.filterSource
    );
  }, [
    filterFromDate,
    filterToDate,
    sorting,
    filterTerminal,
    filterSalesperson,
    filterCustomerGroup,
    filterSource,
    prevFilterData.filterFromDate,
    prevFilterData.filterToDate,
    prevFilterData.sorting,
    prevFilterData.filterTerminal,
    prevFilterData.filterSalesperson,
    prevFilterData.filterCustomerGroup,
    prevFilterData.filterSource,
  ]);

  useEffect(() => {
    // getMachineName();
    getCustomers();
    getSalespersons();
    getTerminals();
    getCustomerGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    generateSummeryReport();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(tReport('NC Sales Summary Report'), filterFromDate, filterToDate, headerData, filteredItems, {
      [tCommon('Total NC Amount')]: `${shop?.currency || ''} ${summary.totalNcAmount}`,
    });
  };

  const getCustomerGroups = async () => {
    try {
      const res = await CustomerService.getCustomerGroups();
      setCustomerGroups(res);
    } catch (err) {
      console.error('Failed to fetch customer groups', err);
    }
  };

  const generateSummeryReport = () => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('NC_BILL_SUMMARY');
    setFilterFromDate(dynamicStartDate);
    setFilterToDate(dynamicEndDate);
    loadBillSummaryReports();
    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: dynamicStartDate,
      filterToDate: dynamicEndDate,
      sorting,
      filterSalesperson,
      filterTerminal,
      filterCustomerGroup,
      filterSource,
    });
  };

  const loadBillSummaryReports = async ({
    startTime,
    endTime,
    sort = sorting,
    cashier_ids = filterSalesperson,
    terminal_ids = filterTerminal,
    customer_group_ids = filterCustomerGroup,
    sources = filterSource,
  } = {}) => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('NC_BILL_SUMMARY');

    const start = startTime || dynamicStartDate;
    const end = endTime || dynamicEndDate;

    if (DateTimeHelpers.reportDateLimit(start, end)) {
      setDateLimitPopup(true);
      return;
    }

    setLoadingIndicator(true);
    try {
      const data = {
        filter_start_time: DateTimeHelpers.getTimezoneOffsetDate(start),
        filter_end_time: DateTimeHelpers.getTimezoneOffsetDate(end),
        terminal_ids: terminal_ids.length ? terminal_ids : null,
        cashier_ids: cashier_ids.length ? cashier_ids : null,
        sort_bill_num: sort === 'default' ? undefined : sort,
        customer_group_ids: customer_group_ids.length ? customer_group_ids : null,
        sources: sources.length ? sources : null,
      };

      const resp = await BillSummaryReportService.getNcBillSummaryReport(data);
      setItems(resp);
      setFilteredItems(resp);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }

    setLoadingIndicator(false);
  };

  const getCustomers = async () => {
    const res = await CustomerService.getCustomers();
    setCustomers(res);
  };

  const getSalespersons = async () => {
    const res = await SalespersonsService.getSalesPersons();
    setSalesPersons(res);
  };

  const getTerminals = async () => {
    const res = await TerminalService.getTerminals();
    setTerminals(res);
  };

  const toCsvBtnPressed = async () => {
    const csv = CsvHelper.getString(items, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `NC-SALES-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const handleCloseSaleDetailsDialog = () => {
    setSelectedBill(null);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setFilteredItems(
      items.filter(
        (item) =>
          item.bill_no.toLowerCase().includes(value.toLowerCase()) ||
          item.date.toLowerCase().includes(value.toLowerCase()) ||
          item.time.toLowerCase().includes(value.toLowerCase()) ||
          (item.customer_name && item.customer_name.toLowerCase().includes(value.toLowerCase()))
      )
    );
  };

  const _getBillReportSummary = (items) => {
    let totalNcAmount = 0;
    items.forEach((item) => {
      totalNcAmount += item.nc_total;
    });

    return {
      totalNcAmount: ShopsHelper.getAmountFormatted(shop, totalNcAmount),
    };
  };

  const headerData = [
    {
      label: tCommon('Bill No'),
      id: 'bill_no',
      type: 'callback',
      viewRender: (obj) => (obj.bill_no ? obj.bill_no : obj.fid),
    },
    {
      label: tCommon('Date'),
      id: 'date',
      type: 'text',
    },
    {
      label: tCommon('Time'),
      id: 'time',
      type: 'callback',
      viewRender: (item) => DateTimeHelpers.formatServerTimeSegment(item.time),
    },
    {
      label: tCommon('Customer Name'),
      id: 'customer_name',
      type: 'text',
    },
    {
      label: tCommon('Mobile'),
      id: 'customer_mobile',
      type: 'text',
    },
    {
      label: tCommon('Tax ID'),
      id: 'customer_tax_id',
      type: 'callback',
      viewRender: (item) => {
        return item.customer_tax_id || '-';
      },
    },
    {
      label: tCommon('Total'),
      id: 'total',
      type: 'callback',
      viewRender: (item) => {
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.nc_total || 0)}`;
      },
    },
    {
      label: tCommon('Actions'),
      id: 'download',
      cssClass: 'no-print',
      cssClassHeader: 'no-print',
      type: 'callback',
      viewRender: (bill) => (
        <Grid className={styles.btnGroup}>
          <Tooltip title={tCommon('View Items')} onClick={() => setSelectedBill(bill)}>
            <RemoveRedEyeIcon className={styles.iconBtn} />
          </Tooltip>
          {shop?.web_invoice_format === INVOICE_LAYOUTS.A4_BACKEND || shop?.web_invoice_format === INVOICE_LAYOUTS.A5_BACKEND || !shop?.web_invoice_format ? (
            <DownloadInvoice bill={bill} />
          ) : (
            <SaleInvoice bill={bill} />
          )}
        </Grid>
      ),
    },
  ];

  const summary = _getBillReportSummary(filteredItems);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            {tCommon('CSV')}
          </Button>
        </div>
      </div>
      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}>
            <h2 className={styles.subTitle}>
              {tReport('NC Sales Summary Report from {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </div>
          <div className={styles.filerInputSec}>
            <div className={styles.searchSec}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  handleSearch(e.target.value);
                }}
                className={styles.searchInput}
                placeholder={tReport('Search by bill no, date')}
              />
              <Tooltip title={tCommon('Search')} placement="bottom">
                <SearchIcon className={styles.searchIcon} />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className={styles.actionButtons}>
          <div className={styles.filterDiv}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="NC_BILL_SUMMARY"
            />
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Terminal')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  displayEmpty
                  value={filterTerminal}
                  onChange={(e) => setFilterTerminal(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterTerminal) => {
                    if (filterTerminal.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = filterTerminal.map((id) => terminals.find((tr) => tr.id === id).name);
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {terminals.map((terminal) => (
                    <MenuItem key={terminal.id} value={terminal.id}>
                      <Checkbox checked={filterTerminal.includes(terminal.id)} color="primary" />
                      <ListItemText primary={terminal.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Salesperson')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  displayEmpty
                  value={filterSalesperson}
                  onChange={(e) => setFilterSalesperson(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterSalesperson) => {
                    if (filterSalesperson.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = filterSalesperson.map(
                      (id) => salesPersons.find((person) => person.id === id).name
                    );
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {salesPersons.map((salesperson) => (
                    <MenuItem key={salesperson.id} value={salesperson.id}>
                      <Checkbox checked={filterSalesperson.includes(salesperson.id)} color="primary" />
                      <ListItemText primary={salesperson?.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Bill no order')}</label>
            </div>
            <div>
              <TextField
                select
                name="billNo"
                size="small"
                variant="outlined"
                color="primary"
                style={{ width: 135 }}
                value={sorting}
                onChange={(event) => {
                  setSorting(event.target.value);
                }}
                displayEmpty
              >
                <MenuItem value={'default'}>
                  <em>{tCommon('Default')}</em>
                </MenuItem>
                <MenuItem value={'DESC'}>{tCommon('Descending')}</MenuItem>
                <MenuItem value={'ASC'}>{tCommon('Ascending')}</MenuItem>
              </TextField>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Customer Group')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  multiple
                  displayEmpty
                  value={filterCustomerGroup}
                  onChange={(e) => setFilterCustomerGroup(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(selected) => {
                    if (selected.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = selected
                      .map((id) => customerGroups.find((group) => group.id === id)?.name)
                      .filter(Boolean);
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {customerGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      <Checkbox checked={filterCustomerGroup.includes(group.id)} color="primary" />
                      <ListItemText primary={group.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Sale source')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  multiple
                  displayEmpty
                  value={filterSource}
                  onChange={(e) => setFilterSource(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(selected) => {
                    if (selected.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = selected
                      .map((id) => SALE_SOURCES.find((source) => source.id === id)?.label)
                      .filter(Boolean);
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {SALE_SOURCES.map((source) => (
                    <MenuItem key={source.id} value={source.id}>
                      <Checkbox checked={filterSource.includes(source.id)} color="primary" />
                      <ListItemText primary={source.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Generate Report')}</label>
            </div>
            <div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={generateSummeryReport}
                disabled={isShowGenerateButton === false || loadingIndicator}
              >
                {loadingIndicator ? (
                  <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                ) : (
                  <SyncIcon className={styles.actionBtnIcon} />
                )}
                {tCommon('Generate')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {isShowGenerateButton === null ? (
        <Info
          title={tReport('Set filters and click generate button')}
          content={tReport(
            'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
          )}
        />
      ) : (
        items &&
        (filteredItems && filteredItems.length ? (
          <div className={styles.tableContainer}>
            <Grayout open={isShowGenerateButton} />
            <DataTable
              columns={headerData}
              rows={filteredItems ? filteredItems : items}
              rowKey="id"
              summary={{
                date: tCommon('Total'),
                total: `${shop?.currency || ''} ${summary.totalNcAmount}`,
              }}
              pagination={true}
            />
          </div>
        ) : (
          !loadingIndicator && (
            <Info
              severity="warning"
              title={tReport('Oh no, there are no sales matching to your filter')}
              content={tReport(
                'There are no sales matching to your filter. Please verify your inputs to generate report.'
              )}
            />
          )
        ))
      )}

      {dateLimitPopup && (
        <DateLimitDialog
          handleClose={() => {
            setDateLimitPopup(false);
          }}
        />
      )}

      {selectedBill && (
        <BillDetailsDialog
          handleClose={handleCloseSaleDetailsDialog}
          selectedSale={selectedBill}
          customers={customers}
        />
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(BillSummaryReport);
